import request from "../utils/request"

// User message endpoints
export const getMessages = params => request.get("/messages", params)

export const getMessage = id => request.get(`/messages/${id}`)

export const getUnreadCount = () => request.get("/messages/unread-count")

export const markAsRead = id => request.post(`/messages/${id}/read`)

// Push subscription endpoints
export const subscribeToPush = subscription =>
	request.post("/push/subscribe", {
		endpoint: subscription.endpoint,
		keys: {
			p256dh: subscription.toJSON().keys.p256dh,
			auth: subscription.toJSON().keys.auth,
		},
	})

export const unsubscribeFromPush = endpoint => request.delete("/push/unsubscribe", { endpoint })

// User push subscription management
export const getUserPushSubscriptions = () => request.get("/push/subscriptions")

export const deletePushSubscriptionById = id => request.delete(`/push/subscriptions/${id}`)

export const sendTestToUserDevice = id => request.post(`/push/subscriptions/${id}/test`)

// Admin message endpoints.
//
// The LIST of admin messages is deliberately NOT here: it is getAdminMessages in
// domain/admin.js, which takes a params object because the Messaging screen filters
// by search, dates, recipient type and groups. A second getAdminMessages used to sit
// here taking positional (page, perPage), imported by nothing -- so the obvious
// function to edit in the messaging domain file was the one with no effect on screen.
// Removed 27 September 2026 rather than left as a same-named shadow.
export const getAdminMessage = id => request.get(`/admin/messages/${id}`)

// Who one message went to, a page at a time. getAdminMessage() no longer carries
// the recipient list at all, so the detail modal asks for it separately -- a
// message to every family used to arrive as one several-hundred-row payload
// nobody could page through.
//
// status is "all", "read" or "unread"; per_page is clamped to 100 server-side.
// The answer is { data: [...], meta: { current_page, last_page, per_page, total,
// counts: { all, read, unread } } }, where `total` counts the active filter and
// `counts` always carries all three, so the filter buttons can show their numbers
// without a second request. No email address appears anywhere in it: the
// recipients table has nothing to do with a family's login identity.
export const getAdminMessageRecipients = (id, { status = "all", page = 1, perPage = 25 } = {}) =>
	request.get(`/admin/messages/${id}/recipients?status=${status}&page=${page}&per_page=${perPage}`)

export const sendMessage = data => request.post("/admin/messages", data)

export const deleteMessage = id => request.delete(`/admin/messages/${id}`)

// Admin message groups endpoints
export const getMessageGroups = () => request.get("/admin/message-groups")

export const getMessageGroup = id => request.get(`/admin/message-groups/${id}`)

export const createMessageGroup = data => request.post("/admin/message-groups", data)

export const updateMessageGroup = (id, data) => request.put(`/admin/message-groups/${id}`, data)

export const deleteMessageGroup = id => request.delete(`/admin/message-groups/${id}`)

export const addGroupMembers = (id, memberIds) =>
	request.post(`/admin/message-groups/${id}/members`, { member_ids: memberIds })

export const removeGroupMember = (groupId, userId) =>
	request.delete(`/admin/message-groups/${groupId}/members/${userId}`)

// Admin user search.
//
// `studentsOnly` is opt-in and off by default. This endpoint also feeds the
// messaging recipient picker and the families, message-group and student-group
// member pickers, and messaging can legitimately reach an admin account -- so
// only the Locked order picker asks for students alone. Its results feed a
// screen that now lists students alone, where offering an admin was a dead end.
export const searchUsers = (query, limit = 20, { studentsOnly = false } = {}) =>
	request.get(
		`/admin/users/search?q=${encodeURIComponent(query)}&limit=${limit}${studentsOnly ? "&students_only=1" : ""}`,
	)

export const getAllUsers = () => request.get("/admin/users/all")

// Admin student groups (for messaging)
export const getStudentGroups = () => request.get("/admin/student-groups")
